import { gql } from "@apollo/client";

export const GET_PLATFORM_METRICS = gql`
  query GetPlatformMetrics {
    platformMetrics {
      companies {
        thisMonth
        lastMonth
        total
      }
      cards {
        thisMonth
        lastMonth
        total
      }
      funds {
        available
        incoming
        outgoing
        pastDue
        activeBalance
      }
      transactions {
        healthPercent
        completedTransactions
        totalTransactions
        averageSize
      }
    }
  }
`;

export const GET_MY_PLATFORM = gql`
  query myPlatform {
    myPlatform {
      id
      name
      defaultAppPlanIds
      defaultAddOnPlanIds
    }
  }
`;

export const GET_AVAILABLE_PLANS = gql`
  query availablePlans {
    availablePlans {
      appPlans {
        id
        name
        description
        appFeatureKeys
      }
      addOnPlans {
        id
        name
        description
        appFeatureKeys
      }
    }
  }
`;

export const GET_PLATFORM_CASH_FLOW = gql`
  query GetPlatformCashFlow {
    platformCashFlow {
      currency
      cashFlowData {
        date
        balance
      }
    }
  }
`;

export const GET_PLATFORM_TOP_CATEGORIES = gql`
  query GetPlatformTopCategories {
    platformTopCategories {
      name
      totalSpend
    }
  }
`;

export const GET_PLATFORM_TOP_COMPANIES = gql`
  query GetPlatformTopCompanies {
    platformTopCompanies {
      id
      name
      totalSpend
      changePercent
    }
  }
`;

export const GET_COMPANY_PLANS = gql`
  query companyPlans($companyId: ID!) {
    companyPlans(companyId: $companyId) {
      appPlanIds
      addOnPlanIds
    }
  }
`;

export const GET_PLATFORM_BRANDING = gql`
  query GetPlatformBranding {
    myPlatform {
      id
      name
      branding {
        keys {
          key
          name
          file {
            assetUrl
            filename
          }
        }
      }
    }
  }
`;

export const UPDATE_PLATFORM_BRANDING = gql`
  mutation UpdatePlatformBranding($input: PlatformBrandingInput!) {
    updatePlatformBranding(input: $input) {
      id
      branding {
        keys {
          key
          name
          file {
            assetUrl
            filename
          }
        }
      }
    }
  }
`;

export const GET_PLATFORM_REWARDS_POOLS = gql`
  query platformRewardsPools {
    platformRewardsPools {
      platformAccountId
      currency
      owned
      issuer
      name
      cash
      redeemable
      coveragePercent
      coverageThresholdPercent
      owed
      fundingDue
      pending
      available
      reserved
      outgoing
      clearing
    }
  }
`;

export const GET_PLATFORM_REWARDS_BY_COMPANY = gql`
  query platformRewardsByCompany($platformAccountId: ID!, $currency: String!) {
    platformRewardsByCompany(
      platformAccountId: $platformAccountId
      currency: $currency
    ) {
      companyId
      businessLegalName
      currency
      pending
      available
      outgoing
      withheld
    }
  }
`;

export const GET_PLATFORM_FUNDING_SOURCES = gql`
  query platformFundingSources {
    platformFundingSources {
      id
      accountType
      status
      name
      institutionName
      last4
      currency
      destinationAccountId
      createdAt
      expiresAt
    }
  }
`;

export const GET_PLATFORM_REWARDS_FUNDING = gql`
  query platformRewardsFunding(
    $first: Int
    $after: String
    $platformAccountId: ID
  ) {
    platformRewardsFunding(
      first: $first
      after: $after
      platformAccountId: $platformAccountId
    ) {
      edges {
        cursor
        node {
          id
          ref
          type
          status
          amount
          currency
          transactionDate
          settledAt
          companyId
          platformAccountId
          description
        }
      }
      pageInfo {
        hasNextPage
        endCursor
        total
      }
    }
  }
`;

export const BEGIN_PLATFORM_FUNDING_SOURCE = gql`
  mutation beginPlatformFundingSource(
    $destinationAccountId: ID!
    $name: String!
  ) {
    beginPlatformFundingSource(
      destinationAccountId: $destinationAccountId
      name: $name
    ) {
      id
      setupIntentClientSecret
      publishableKey
      plaidLinkToken
      status
      name
      currency
      destinationAccountId
      expiresAt
    }
  }
`;

export const REMOVE_PLATFORM_FUNDING_SOURCE = gql`
  mutation removePlatformFundingSource($id: ID!) {
    removePlatformFundingSource(id: $id) {
      id
      status
    }
  }
`;

export const COMPLETE_PLATFORM_FUNDING_SOURCE = gql`
  mutation completePlatformFundingSource(
    $id: ID!
    $input: CompletePlatformFundingSourceInput!
  ) {
    completePlatformFundingSource(id: $id, input: $input) {
      id
      status
      institutionName
      last4
      microDepositUrl
    }
  }
`;

export const FUND_PLATFORM_REWARDS = gql`
  mutation fundPlatformRewards($input: FundPlatformRewardsInput!) {
    fundPlatformRewards(input: $input) {
      id
      type
      status
      amount
      currency
    }
  }
`;

const REWARD_RULE_FIELDS = `
  id
  name
  scope
  defaultCategoryId
  categoryName
  merchantId
  merchantName
  companies {
    id
    name
  }
  ratePercent
  priority
  startsOn
  endsOn
  monthlyCap
  capCurrency
  enabled
  status
`;

export const GET_PLATFORM_REWARD_RULES = gql`
  query platformRewardRules {
    platformRewardRules {
      ${REWARD_RULE_FIELDS}
    }
  }
`;

export const GET_PLATFORM_REWARD_RULE_SUMMARY = gql`
  query platformRewardRuleSummary($platformAccountId: ID!, $currency: String!) {
    platformRewardRuleSummary(
      platformAccountId: $platformAccountId
      currency: $currency
    ) {
      blendedRatePercent
      accruedLast30Days
      outstanding
      currency
      maturityDays
      coverageThresholdPercent
    }
  }
`;

export const GET_REWARD_CATEGORIES = gql`
  query rewardCategories {
    rewardCategories {
      id
      name
    }
  }
`;

export const GET_PLATFORM_REWARDS_AVAILABLE = gql`
  query platformRewardsAvailable {
    platformRewardsAvailable
  }
`;

export const PREVIEW_REWARD_PURCHASE = gql`
  query previewRewardPurchase($input: RewardPurchasePreviewInput!) {
    previewRewardPurchase(input: $input) {
      rewardAmount
      currency
      winner {
        ${REWARD_RULE_FIELDS}
      }
      candidates {
        outcome
        earnedThisMonth
        rule {
          ${REWARD_RULE_FIELDS}
        }
      }
    }
  }
`;

export const GET_PLATFORM_REWARDS_REDEMPTIONS = gql`
  query platformRewardsRedemptions(
    $first: Int
    $after: String
    $platformAccountId: ID
  ) {
    platformRewardsRedemptions(
      first: $first
      after: $after
      platformAccountId: $platformAccountId
    ) {
      edges {
        cursor
        node {
          id
          type
          status
          amount
          currency
          transactionDate
          companyId
          companyName
          description
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const SAVE_PLATFORM_REWARD_RULE = gql`
  mutation savePlatformRewardRule($id: ID, $input: PlatformRewardRuleInput!) {
    savePlatformRewardRule(id: $id, input: $input) {
      ${REWARD_RULE_FIELDS}
    }
  }
`;

export const SET_PLATFORM_REWARD_RULE_ENABLED = gql`
  mutation setPlatformRewardRuleEnabled($id: ID!, $enabled: Boolean!) {
    setPlatformRewardRuleEnabled(id: $id, enabled: $enabled) {
      ${REWARD_RULE_FIELDS}
    }
  }
`;

export const SEARCH_PLATFORM_COMPANIES = gql`
  query searchPlatformCompanies(
    $searchTerm: String
    $first: Int
    $after: String
  ) {
    getCompanies(searchTerm: $searchTerm, first: $first, after: $after) {
      edges {
        node {
          id
          businessLegalName
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const GET_EMBEDDED_ORIGINS = gql`
  query GetEmbeddedOrigins {
    myPlatform {
      id
      embeddedOrigins
    }
  }
`;

export const SET_EMBEDDED_ORIGINS = gql`
  mutation SetEmbeddedOrigins($origins: [String!]!) {
    setEmbeddedOrigins(origins: $origins)
  }
`;
